import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { selectOrganisationId } from "../../redux/organisationReducer";
import { toast } from "../../core/toast";
import { useNavigate } from "react-router-dom";
import {
  useGetThemeByIdQuery,
  useGetThemesByOrgQuery,
  useChangeThemeStatusMutation,
  useDeleteThemeMutation,
  ThemeDocument,
  Theme,
  useCreateThemeMutation,
  useGetDefaultThemeQuery,
} from "../../services/settings/setting.rtk.api";

import DeleteThemeModal from "./DelteThemeModal";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import ScaledIframe, { ScaledIframeRef } from "./ScaledIframe";
import {
  useGetOrganisationByIdQuery,
  useGetOrganisationsQuery,
} from "../../services";

const ThemeSettings: React.FC = () => {
  const { theme } = useTheme();
  const iframeRef = useRef<ScaledIframeRef>(null);

  // THEME CLASSES    const [changeThemeStatus,{isLoding:isChangingStatus}] = useChangeThemeStatusMutation();
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = theme === "dark" ? "text-white" : "text-gray-900";
  const crumbLink = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const organisationId = useSelector(selectOrganisationId);
  const navigate = useNavigate();
  const { data: ThemeList, isLoading } = useGetThemesByOrgQuery(
    organisationId || skipToken,
  );

  const [createTheme] = useCreateThemeMutation();

  const { data: defaultTheme, isLoading: isLoadingDefaults } =
    useGetDefaultThemeQuery();

  const isThemsBothLoading = isLoading || isLoadingDefaults;
  const combinedThemes = [
    // If default theme exists, spread it and inject isDefault: true
    ...(defaultTheme ? [{ ...defaultTheme, isDefault: true }] : []),

    // Spread the rest of the themes
    ...(ThemeList || []),
  ];
  const [changeThemeStatus, { data: changeData, isLoading: changeLoading }] =
    useChangeThemeStatusMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<any>(null);
  const [deleteTheme, { isLoading: deleteLoading }] = useDeleteThemeMutation();
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [previewDevice, setPreviewDevice] = useState<"tv" | "staff">("tv");
  const [previewMode, setPreviewMode] = useState<"dark" | "light">("dark");
  const [previewThemeData, setPreviewThemeData] = useState<Theme | null>(null);

  const previewUrl = useMemo(() => {
    if (!previewThemeData) return "";
    try {
      const assetBaseUrl =
        import.meta.env.VITE_APP_ASSET_URL || "https://dev.assets.gymstream.app";
      const getThemeAssetUrl = (themeObj: any, fileObj: any) => {
        if (!fileObj) return null;
        if (typeof fileObj === "string") return fileObj;
        if (fileObj && typeof fileObj === "object" && fileObj.key) {
          const folder = themeObj?.access === "GLOBAL" ? "app" : "subscriber";
          return `${assetBaseUrl}/files/${folder}/themes/${fileObj.key}`;
        }
        return null;
      };

      // Transform branding logo and asset banners to full URL strings so the preview iframe can render them immediately
      const payload = {
        ...previewThemeData,
        lightTheme: previewThemeData.lightTheme
          ? {
              ...previewThemeData.lightTheme,
              branding: previewThemeData.lightTheme.branding
                ? {
                    ...previewThemeData.lightTheme.branding,
                    logo: getThemeAssetUrl(
                      previewThemeData,
                      (previewThemeData.lightTheme.branding as any)?.logo,
                    ),
                  }
                : undefined,
              assets: previewThemeData.lightTheme.assets
                ? {
                    ...previewThemeData.lightTheme.assets,
                    banners: (previewThemeData.lightTheme.assets as any).banners
                      ? {
                          ...((previewThemeData.lightTheme.assets as any).banners || {}),
                          device: getThemeAssetUrl(
                            previewThemeData,
                            ((previewThemeData.lightTheme.assets as any).banners || {}).device,
                          ),
                          user: getThemeAssetUrl(
                            previewThemeData,
                            ((previewThemeData.lightTheme.assets as any).banners || {}).user,
                          ),
                        }
                      : undefined,
                  }
                : undefined,
            }
          : undefined,
        darkTheme: previewThemeData.darkTheme
          ? {
              ...previewThemeData.darkTheme,
              branding: previewThemeData.darkTheme.branding
                ? {
                    ...previewThemeData.darkTheme.branding,
                    logo: getThemeAssetUrl(
                      previewThemeData,
                      (previewThemeData.darkTheme.branding as any)?.logo,
                    ),
                  }
                : undefined,
              assets: previewThemeData.darkTheme.assets
                ? {
                    ...previewThemeData.darkTheme.assets,
                    banners: (previewThemeData.darkTheme.assets as any).banners
                      ? {
                          ...((previewThemeData.darkTheme.assets as any).banners || {}),
                          device: getThemeAssetUrl(
                            previewThemeData,
                            ((previewThemeData.darkTheme.assets as any).banners || {}).device,
                          ),
                          user: getThemeAssetUrl(
                            previewThemeData,
                            ((previewThemeData.darkTheme.assets as any).banners || {}).user,
                          ),
                        }
                      : undefined,
                  }
                : undefined,
            }
          : undefined,
      };

      const encodeTheme = btoa(
        unescape(encodeURIComponent(JSON.stringify(payload))),
      );
      const baseUrl =
        previewDevice === "tv"
          ? import.meta.env.VITE_TV_APP_URL
          : import.meta.env.VITE_STAFF_APP_URL;
      return `${baseUrl}/preview-theme?theme=${encodeTheme}&mode=${previewMode}`;
    } catch (error) {
      console.error("Failed to generate preview URL", error);
      return "";
    }
  }, [previewThemeData, previewDevice, previewMode]);

  const hasCreatedForOrg = useRef<Record<string, boolean>>({});

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border border-[#2A2A2A] text-gray-300"
      : "bg-white border border-gray-200 text-gray-800";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between bg-[#222222]"
      : "px-6 py-4 border-b border-gray-200 text-gray-900 flex items-center justify-between bg-gray-50";

  const tableHeaderClass =
    theme === "dark"
      ? "text-gray-200 font-semibold"
      : "text-gray-700 font-semibold";

  const tableRowClass =
    theme === "dark"
      ? "border-b border-[#2A2A2A] hover:bg-[#222222]"
      : "border-b border-gray-200 hover:bg-gray-50";

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Added useState for the themes so the toggle works
  const [themes, setThemes] = useState([
    {
      id: 1,
      name: "Default Theme",
      createdOn: "01/04/2026",
      updatedOn: "01/04/2026",
      mode: "Dark\nLight",
      status: "Inactive",
    },
    {
      id: 2,
      name: "Power Gym",
      createdOn: "15/04/2026",
      updatedOn: "25/04/2026",
      mode: "Dark",
      status: "Active",
    },
    {
      id: 3,
      name: "Sample Theme",
      createdOn: "02/04/2026",
      updatedOn: "13/04/2026",
      mode: "Dark\nLight",
      status: "Inactive",
    },
  ]);

  // Added the toggle function

  const toggleThemeStatus = async (id: string) => {
    // 1. Find the current item in the list to determine the next status
    const currentTheme = ThemeList?.find((t) => t._id === id);

    // 2. Guard clause: if theme isn't found or data is still loading, exit
    if (!currentTheme) return;

    // 3. Determine the new status
    // We normalize to uppercase to match your 'ACTIVE' | 'INACTIVE' type
    const newStatus =
      currentTheme.status.toUpperCase() === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      // 4. Call the mutation from your themeApi
      // This triggers the Optimistic Update you wrote in your API slice
      await changeThemeStatus({
        themeId: id,
        status: newStatus,
      }).unwrap();

      // Success toast is already handled in your API's onQueryStarted!
    } catch (err) {
      // Error toast is also handled in your API, but you can log it here
      console.error("Failed to change theme status:", err);
    }
  };
  const { data: orgsData } = useGetOrganisationsQuery();

  const selectedOrgObj = (
    (orgsData as any)?.data ||
    (orgsData as any) ||
    []
  ).find((org: any) => String(org?._id) === String(organisationId));
  const isPremiumOrg = selectedOrgObj?.level?.toUpperCase() === "PREMIUM";

  const handleEditThemeClick = (themeId: string) => {
    navigate(`/theme-settings/${themeId}`);
  };

  const handleCreateThemeClick = () => {
    if (!isPremiumOrg) {
      toast.warning("Purchase a Premium license to access custom themes ");
      return;
    }
    navigate("/theme-settings/create");
  };

  const handleDeleteTheme = async () => {
    if (!selectedTheme?._id) return;
    try {
      await deleteTheme(selectedTheme?._id).unwrap();
      setShowDeleteModal(false);
      // Optional: Redirect back to theme list here
    } catch (error) {
      console.error("Failed to delete theme:", error);
    }
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB"); // Format: DD/MM/YYYY
  };
  const handlePreviewThemeClick = (themeData: Theme) => {
    try {
      console.log(themeData, "themeDat");
      setPreviewThemeData(themeData);
      setPreviewDevice("tv");
      const initialMode =
        (themeData.defaultMode || "DARK").toUpperCase() === "LIGHT"
          ? "light"
          : "dark";
      setPreviewMode(initialMode);
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Failed to handle preview theme click:", error);
    }
  };

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: "margin-left 300ms ease-in-out, width 300ms ease-in-out",
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        <h1 className={`text-2xl font-semibold ${heading} mb-2`}>Settings</h1>
        <div className="text-sm flex items-center gap-2">
          {/* Home Placeholder SVG */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-400"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <Link
            to="/"
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          <span className="text-[#B6CB24] font-normal">Settings</span>
        </div>
      </div>

      {/* Main Table Card */}
      <div
        className={`${cardClass} rounded-xl overflow-hidden shadow-md w-full`}
      >
        <div className={cardHeader}>
          <span className="text-lg font-semibold">All Preview Themes</span>
          <button
            onClick={handleCreateThemeClick}
            className={`${!isPremiumOrg ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            <svg
              width="198"
              height="42"
              viewBox="0 0 198 42"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="198"
                height="42"
                rx="10"
                fill="url(#paint0_radial_5892_1767)"
                fill-opacity="0.4"
              />
              <rect
                x="0.5"
                y="0.5"
                width="197"
                height="41"
                rx="9.5"
                stroke="url(#paint1_linear_5892_1767)"
                stroke-opacity="0.88"
              />
              <path
                d="M28 11.0001C33.5228 11.0001 38 15.4773 38 21.0001C38 26.523 33.5228 31.0001 28 31.0001C22.4771 31.0001 18 26.523 18 21.0001M24.909 11.487C23.9 11.8146 22.9614 12.2983 22.1215 12.9095M19.9094 15.1216C19.2981 15.9616 18.8143 16.9004 18.4867 17.9096"
                stroke="#F3F3F6"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M28 17.0001V25.0001M32 21.0001H24"
                stroke="#C6D92C"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M61.7195 19.2536H59.8601C59.8071 18.9486 59.7093 18.6785 59.5668 18.4432C59.4242 18.2045 59.2469 18.0024 59.0348 17.8366C58.8227 17.6709 58.5807 17.5466 58.3089 17.4638C58.0405 17.3776 57.7505 17.3345 57.4389 17.3345C56.8854 17.3345 56.3949 17.4737 55.9673 17.7521C55.5398 18.0272 55.205 18.4316 54.9631 18.9652C54.7211 19.4955 54.6001 20.1435 54.6001 20.9091C54.6001 21.688 54.7211 22.3442 54.9631 22.8778C55.2083 23.4081 55.5431 23.8092 55.9673 24.081C56.3949 24.3494 56.8838 24.4837 57.4339 24.4837C57.7389 24.4837 58.0239 24.4439 58.2891 24.3643C58.5575 24.2815 58.7978 24.1605 59.0099 24.0014C59.2254 23.8423 59.406 23.6468 59.5518 23.4148C59.701 23.1828 59.8037 22.9176 59.8601 22.6193L61.7195 22.6293C61.6499 23.1132 61.4991 23.5672 61.267 23.9915C61.0384 24.4157 60.7384 24.7902 60.3672 25.1151C59.996 25.4366 59.5618 25.6884 59.0646 25.8707C58.5675 26.0497 58.0156 26.1392 57.4091 26.1392C56.5142 26.1392 55.7154 25.9321 55.0128 25.5178C54.3101 25.1035 53.7566 24.5052 53.3523 23.723C52.9479 22.9408 52.7457 22.0028 52.7457 20.9091C52.7457 19.812 52.9496 18.8741 53.3572 18.0952C53.7649 17.313 54.3201 16.7147 55.0227 16.3004C55.7254 15.8861 56.5208 15.679 57.4091 15.679C57.9759 15.679 58.5028 15.7585 58.9901 15.9176C59.4773 16.0767 59.9115 16.3104 60.2926 16.6186C60.6738 16.9235 60.987 17.2981 61.2322 17.7422C61.4808 18.183 61.6432 18.6868 61.7195 19.2536ZM63.329 26V18.3636H65.074V19.6364H65.1536C65.2928 19.1955 65.5314 18.8558 65.8695 18.6172C66.2109 18.3752 66.6003 18.2543 67.0378 18.2543C67.1373 18.2543 67.2483 18.2592 67.3709 18.2692C67.4969 18.2758 67.6013 18.2874 67.6841 18.304V19.9595C67.6079 19.933 67.4869 19.9098 67.3212 19.8899C67.1588 19.8667 67.0014 19.8551 66.8489 19.8551C66.5208 19.8551 66.2258 19.9264 65.964 20.0689C65.7054 20.2081 65.5016 20.402 65.3525 20.6506C65.2033 20.8991 65.1287 21.1858 65.1287 21.5107V26H63.329ZM71.946 26.1491C71.1804 26.1491 70.5192 25.9901 69.9624 25.6719C69.4089 25.3504 68.983 24.8963 68.6847 24.3097C68.3864 23.7197 68.2372 23.0253 68.2372 22.2266C68.2372 21.4411 68.3864 20.7517 68.6847 20.1584C68.9863 19.5618 69.4072 19.0978 69.9474 18.7663C70.4877 18.4316 71.1224 18.2642 71.8516 18.2642C72.3222 18.2642 72.7663 18.3404 73.1839 18.4929C73.6049 18.642 73.9761 18.8741 74.2976 19.1889C74.6224 19.5038 74.8776 19.9048 75.0632 20.392C75.2488 20.8759 75.3416 21.4527 75.3416 22.1222V22.674H69.0824V21.4609H73.6165C73.6132 21.1162 73.5386 20.8097 73.3928 20.5412C73.2469 20.2694 73.0431 20.0556 72.7812 19.8999C72.5227 19.7441 72.2211 19.6662 71.8764 19.6662C71.5085 19.6662 71.1854 19.7557 70.907 19.9347C70.6286 20.1103 70.4115 20.3423 70.2557 20.6307C70.1032 20.9157 70.0253 21.2289 70.022 21.5703V22.6293C70.022 23.0734 70.1032 23.4545 70.2656 23.7727C70.428 24.0876 70.6551 24.3295 70.9467 24.4986C71.2384 24.6643 71.5798 24.7472 71.9709 24.7472C72.2327 24.7472 72.4697 24.7107 72.6818 24.6378C72.8939 24.5616 73.0779 24.4505 73.2337 24.3047C73.3894 24.1589 73.5071 23.9782 73.5866 23.7628L75.267 23.9517C75.161 24.3958 74.9588 24.7836 74.6605 25.1151C74.3655 25.4432 73.9877 25.6984 73.527 25.8807C73.0663 26.0597 72.5393 26.1491 71.946 26.1491ZM79.0616 26.1541C78.5777 26.1541 78.1419 26.0679 77.7541 25.8956C77.3696 25.7199 77.0647 25.4614 76.8393 25.12C76.6172 24.7786 76.5062 24.3577 76.5062 23.8572C76.5062 23.4264 76.5858 23.0701 76.7449 22.7884C76.9039 22.5066 77.121 22.2812 77.3961 22.1122C77.6712 21.9432 77.9811 21.8156 78.3258 21.7294C78.6738 21.6399 79.0334 21.5753 79.4047 21.5355C79.8521 21.4891 80.215 21.4477 80.4934 21.4112C80.7718 21.3714 80.974 21.3118 81.1 21.2322C81.2292 21.1494 81.2939 21.0218 81.2939 20.8494V20.8196C81.2939 20.4451 81.1828 20.1551 80.9608 19.9496C80.7387 19.7441 80.4189 19.6413 80.0012 19.6413C79.5604 19.6413 79.2108 19.7375 78.9522 19.9297C78.697 20.1219 78.5247 20.349 78.4352 20.6108L76.7548 20.3722C76.8874 19.9081 77.1061 19.5204 77.411 19.2088C77.716 18.8939 78.0888 18.6586 78.5297 18.5028C78.9705 18.3438 79.4577 18.2642 79.9913 18.2642C80.3592 18.2642 80.7254 18.3073 81.09 18.3935C81.4546 18.4796 81.7877 18.6222 82.0893 18.821C82.3909 19.0166 82.6329 19.2834 82.8152 19.6214C83.0008 19.9595 83.0936 20.3821 83.0936 20.8892V26H81.3635V24.951H81.3038C81.1944 25.1631 81.0403 25.362 80.8414 25.5476C80.6459 25.7299 80.399 25.8774 80.1007 25.9901C79.8057 26.0994 79.4593 26.1541 79.0616 26.1541ZM79.5289 24.8317C79.8902 24.8317 80.2034 24.7604 80.4686 24.6179C80.7337 24.4721 80.9376 24.2798 81.0801 24.0412C81.2259 23.8026 81.2988 23.5424 81.2988 23.2607V22.3608C81.2425 22.4072 81.1464 22.4503 81.0105 22.4901C80.8779 22.5298 80.7288 22.5646 80.563 22.5945C80.3973 22.6243 80.2333 22.6508 80.0708 22.674C79.9084 22.6972 79.7676 22.7171 79.6483 22.7337C79.3798 22.7701 79.1395 22.8298 78.9274 22.9126C78.7153 22.9955 78.5479 23.1115 78.4252 23.2607C78.3026 23.4065 78.2413 23.5954 78.2413 23.8274C78.2413 24.1589 78.3623 24.4091 78.6042 24.5781C78.8462 24.7472 79.1544 24.8317 79.5289 24.8317ZM88.7164 18.3636V19.7557H84.3265V18.3636H88.7164ZM85.4103 16.5341H87.21V23.7031C87.21 23.9451 87.2465 24.1307 87.3194 24.2599C87.3957 24.3859 87.4951 24.4721 87.6177 24.5185C87.7404 24.5649 87.8762 24.5881 88.0254 24.5881C88.1381 24.5881 88.2408 24.5798 88.3336 24.5632C88.4297 24.5466 88.5027 24.5317 88.5524 24.5185L88.8556 25.9254C88.7595 25.9586 88.622 25.995 88.443 26.0348C88.2673 26.0746 88.0519 26.0978 87.7967 26.1044C87.3459 26.1177 86.9399 26.0497 86.5787 25.9006C86.2174 25.7481 85.9307 25.5128 85.7186 25.1946C85.5098 24.8764 85.407 24.4787 85.4103 24.0014V16.5341ZM93.5202 26.1491C92.7546 26.1491 92.0934 25.9901 91.5366 25.6719C90.9831 25.3504 90.5572 24.8963 90.2589 24.3097C89.9606 23.7197 89.8114 23.0253 89.8114 22.2266C89.8114 21.4411 89.9606 20.7517 90.2589 20.1584C90.5605 19.5618 90.9814 19.0978 91.5217 18.7663C92.0619 18.4316 92.6966 18.2642 93.4258 18.2642C93.8964 18.2642 94.3406 18.3404 94.7582 18.4929C95.1791 18.642 95.5503 18.8741 95.8718 19.1889C96.1966 19.5038 96.4518 19.9048 96.6374 20.392C96.823 20.8759 96.9158 21.4527 96.9158 22.1222V22.674H90.6566V21.4609H95.1907C95.1874 21.1162 95.1128 20.8097 94.967 20.5412C94.8211 20.2694 94.6173 20.0556 94.3555 19.8999C94.0969 19.7441 93.7953 19.6662 93.4506 19.6662C93.0827 19.6662 92.7596 19.7557 92.4812 19.9347C92.2028 20.1103 91.9857 20.3423 91.8299 20.6307C91.6774 20.9157 91.5996 21.2289 91.5962 21.5703V22.6293C91.5962 23.0734 91.6774 23.4545 91.8398 23.7727C92.0022 24.0876 92.2293 24.3295 92.521 24.4986C92.8126 24.6643 93.154 24.7472 93.5451 24.7472C93.8069 24.7472 94.0439 24.7107 94.256 24.6378C94.4682 24.5616 94.6521 24.4505 94.8079 24.3047C94.9637 24.1589 95.0813 23.9782 95.1609 23.7628L96.8413 23.9517C96.7352 24.3958 96.533 24.7836 96.2347 25.1151C95.9397 25.4432 95.5619 25.6984 95.1012 25.8807C94.6405 26.0597 94.1135 26.1491 93.5202 26.1491ZM110.358 15.8182V26H108.717L103.92 19.0646H103.835V26H101.991V15.8182H103.641L108.434 22.7585H108.523V15.8182H110.358ZM115.669 26.1491C114.903 26.1491 114.242 25.9901 113.685 25.6719C113.132 25.3504 112.706 24.8963 112.407 24.3097C112.109 23.7197 111.96 23.0253 111.96 22.2266C111.96 21.4411 112.109 20.7517 112.407 20.1584C112.709 19.5618 113.13 19.0978 113.67 18.7663C114.21 18.4316 114.845 18.2642 115.574 18.2642C116.045 18.2642 116.489 18.3404 116.907 18.4929C117.328 18.642 117.699 18.8741 118.02 19.1889C118.345 19.5038 118.6 19.9048 118.786 20.392C118.971 20.8759 119.064 21.4527 119.064 22.1222V22.674H112.805V21.4609H117.339C117.336 21.1162 117.261 20.8097 117.115 20.5412C116.97 20.2694 116.766 20.0556 116.504 19.8999C116.245 19.7441 115.944 19.6662 115.599 19.6662C115.231 19.6662 114.908 19.7557 114.63 19.9347C114.351 20.1103 114.134 20.3423 113.978 20.6307C113.826 20.9157 113.748 21.2289 113.745 21.5703V22.6293C113.745 23.0734 113.826 23.4545 113.988 23.7727C114.151 24.0876 114.378 24.3295 114.669 24.4986C114.961 24.6643 115.302 24.7472 115.694 24.7472C115.955 24.7472 116.192 24.7107 116.404 24.6378C116.617 24.5616 116.801 24.4505 116.956 24.3047C117.112 24.1589 117.23 23.9782 117.309 23.7628L118.99 23.9517C118.884 24.3958 118.681 24.7836 118.383 25.1151C118.088 25.4432 117.71 25.6984 117.25 25.8807C116.789 26.0597 116.262 26.1491 115.669 26.1491ZM122.122 26L119.964 18.3636H121.799L123.141 23.733H123.211L124.583 18.3636H126.397L127.77 23.7031H127.844L129.167 18.3636H131.006L128.843 26H126.969L125.537 20.8395H125.433L124.001 26H122.122ZM135.367 17.3643V15.8182H143.491V17.3643H140.344V26H138.514V17.3643H135.367ZM146.777 21.5256V26H144.977V15.8182H146.737V19.6612H146.827C147.006 19.2304 147.283 18.8906 147.657 18.642C148.035 18.3902 148.516 18.2642 149.099 18.2642C149.629 18.2642 150.092 18.3752 150.486 18.5973C150.88 18.8194 151.185 19.1442 151.401 19.5717C151.619 19.9993 151.729 20.5213 151.729 21.1378V26H149.929V21.4162C149.929 20.9025 149.797 20.5031 149.531 20.218C149.27 19.9297 148.902 19.7855 148.428 19.7855C148.11 19.7855 147.825 19.8551 147.573 19.9943C147.324 20.1302 147.128 20.3274 146.986 20.5859C146.847 20.8445 146.777 21.1577 146.777 21.5256ZM156.944 26.1491C156.178 26.1491 155.517 25.9901 154.96 25.6719C154.407 25.3504 153.981 24.8963 153.683 24.3097C153.384 23.7197 153.235 23.0253 153.235 22.2266C153.235 21.4411 153.384 20.7517 153.683 20.1584C153.984 19.5618 154.405 19.0978 154.945 18.7663C155.486 18.4316 156.12 18.2642 156.85 18.2642C157.32 18.2642 157.764 18.3404 158.182 18.4929C158.603 18.642 158.974 18.8741 159.296 19.1889C159.62 19.5038 159.876 19.9048 160.061 20.392C160.247 20.8759 160.34 21.4527 160.34 22.1222V22.674H154.08V21.4609H158.615C158.611 21.1162 158.537 20.8097 158.391 20.5412C158.245 20.2694 158.041 20.0556 157.779 19.8999C157.521 19.7441 157.219 19.6662 156.874 19.6662C156.507 19.6662 156.183 19.7557 155.905 19.9347C155.627 20.1103 155.41 20.3423 155.254 20.6307C155.101 20.9157 155.023 21.2289 155.02 21.5703V22.6293C155.02 23.0734 155.101 23.4545 155.264 23.7727C155.426 24.0876 155.653 24.3295 155.945 24.4986C156.236 24.6643 156.578 24.7472 156.969 24.7472C157.231 24.7472 157.468 24.7107 157.68 24.6378C157.892 24.5616 158.076 24.4505 158.232 24.3047C158.387 24.1589 158.505 23.9782 158.585 23.7628L160.265 23.9517C160.159 24.3958 159.957 24.7836 159.659 25.1151C159.364 25.4432 158.986 25.6984 158.525 25.8807C158.064 26.0597 157.537 26.1491 156.944 26.1491ZM161.862 26V18.3636H163.582V19.6612H163.672C163.831 19.2237 164.094 18.8823 164.462 18.6371C164.83 18.3885 165.269 18.2642 165.78 18.2642C166.297 18.2642 166.733 18.3902 167.087 18.642C167.445 18.8906 167.697 19.2304 167.843 19.6612H167.923C168.092 19.237 168.377 18.8989 168.778 18.647C169.182 18.3918 169.661 18.2642 170.214 18.2642C170.917 18.2642 171.491 18.4863 171.935 18.9304C172.379 19.3745 172.601 20.0225 172.601 20.8743V26H170.796V21.1527C170.796 20.6787 170.67 20.3324 170.418 20.1136C170.166 19.8916 169.858 19.7805 169.494 19.7805C169.059 19.7805 168.72 19.9164 168.474 20.1882C168.232 20.4567 168.112 20.8063 168.112 21.2372V26H166.347V21.0781C166.347 20.6837 166.227 20.3688 165.989 20.1335C165.753 19.8982 165.445 19.7805 165.064 19.7805C164.805 19.7805 164.57 19.8468 164.358 19.9794C164.146 20.1087 163.977 20.2926 163.851 20.5312C163.725 20.7666 163.662 21.0417 163.662 21.3565V26H161.862ZM177.807 26.1491C177.042 26.1491 176.381 25.9901 175.824 25.6719C175.27 25.3504 174.844 24.8963 174.546 24.3097C174.248 23.7197 174.099 23.0253 174.099 22.2266C174.099 21.4411 174.248 20.7517 174.546 20.1584C174.848 19.5618 175.269 19.0978 175.809 18.7663C176.349 18.4316 176.984 18.2642 177.713 18.2642C178.184 18.2642 178.628 18.3404 179.045 18.4929C179.466 18.642 179.837 18.8741 180.159 19.1889C180.484 19.5038 180.739 19.9048 180.925 20.392C181.11 20.8759 181.203 21.4527 181.203 22.1222V22.674H174.944V21.4609H179.478C179.474 21.1162 179.4 20.8097 179.254 20.5412C179.108 20.2694 178.904 20.0556 178.643 19.8999C178.384 19.7441 178.082 19.6662 177.738 19.6662C177.37 19.6662 177.047 19.7557 176.768 19.9347C176.49 20.1103 176.273 20.3423 176.117 20.6307C175.965 20.9157 175.887 21.2289 175.883 21.5703V22.6293C175.883 23.0734 175.965 23.4545 176.127 23.7727C176.289 24.0876 176.516 24.3295 176.808 24.4986C177.1 24.6643 177.441 24.7472 177.832 24.7472C178.094 24.7472 178.331 24.7107 178.543 24.6378C178.755 24.5616 178.939 24.4505 179.095 24.3047C179.251 24.1589 179.368 23.9782 179.448 23.7628L181.128 23.9517C181.022 24.3958 180.82 24.7836 180.522 25.1151C180.227 25.4432 179.849 25.6984 179.388 25.8807C178.928 26.0597 178.401 26.1491 177.807 26.1491Z"
                fill="white"
              />
              <defs>
                <radialGradient
                  id="paint0_radial_5892_1767"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(103.901) rotate(90) scale(42 186.458)"
                >
                  <stop stop-color="#C6D92C" />
                  <stop offset="1" stop-color="#535B10" />
                </radialGradient>
                <linearGradient
                  id="paint1_linear_5892_1767"
                  x1="0"
                  y1="21"
                  x2="198"
                  y2="21"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#C6D92C" />
                  <stop offset="0.533654" stop-color="#EDF5C0" />
                  <stop offset="1" stop-color="#C6D92C" />
                </linearGradient>
              </defs>
            </svg>
          </button>
        </div>

        <div className="overflow-auto max-h-[calc(90vh-250px)]">
          {isThemsBothLoading ? (
            <div className="flex flex-col items-center justify-center">
              <div className="w-12 h-12 border-4  border-[#2A2A2A] border-t-[#C6D92C] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-400 font-medium">Loading Themes...</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead
                className={theme === "dark" ? "bg-[#2A2A2A]" : "bg-gray-100"}
              >
                <tr>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Theme Name
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Created On
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>
                    Updated On
                  </th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Mode</th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Status</th>
                  <th className={`px-6 py-4 ${tableHeaderClass}`}>Action</th>
                </tr>
              </thead>
              <tbody>
                {combinedThemes?.map((t) => (
                  <tr key={t._id} className={tableRowClass}>
                    <td className="px-6 py-4">
                      {t.name}{" "}
                      {t.isDefault ? (
                        <span className="text-green-600 text-sm">
                          {" "}
                          (Default)
                        </span>
                      ) : (
                        <></>
                      )}{" "}
                    </td>
                    <td className="px-6 py-4">{formatDate(t.createdAt)}</td>
                    <td className="px-6 py-4">{formatDate(t.updatedAt)}</td>
                    <td className="px-6 py-4 whitespace-pre-line leading-snug">
                      {t.mode}
                    </td>
                    <td className="px-6 py-4">{t.status}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Status Button */}
                        <div className="w-[148px] flex justify-start">
                          {!t.isDefault && (
                            <>
                              {t.status === "Active" ? (
                                <button
                                  className=""
                                  onClick={() => toggleThemeStatus(t._id)}
                                >
                                  <svg
                                    width="123"
                                    height="36"
                                    viewBox="0 0 123 36"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      width="123"
                                      height="36"
                                      rx="10"
                                      fill="url(#paint0_radial_5892_1829)"
                                      fill-opacity="0.8"
                                    />
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="122"
                                      height="35"
                                      rx="9.5"
                                      stroke="url(#paint1_linear_5892_1829)"
                                      stroke-opacity="0.8"
                                    />
                                    <path
                                      d="M35 18C35 19.6569 33.6569 21 32 21C30.3431 21 29 19.6569 29 18C29 16.3431 30.3431 15 32 15C33.6569 15 35 16.3431 35 18Z"
                                      stroke="#F3F3F6"
                                      stroke-width="1.5"
                                    />
                                    <path
                                      d="M32 12H24C20.6863 12 18 14.6863 18 18C18 21.3137 20.6863 24 24 24H32C35.3137 24 38 21.3137 38 18C38 14.6863 35.3137 12 32 12Z"
                                      stroke="#F3F3F6"
                                      stroke-width="1.5"
                                    />
                                    <path
                                      d="M53.0043 23V12.8182H59.6264V14.3643H54.8487V17.1286H59.2834V18.6747H54.8487V21.4538H59.6662V23H53.0043ZM63.2283 18.5256V23H61.4286V15.3636H63.1488V16.6612H63.2383C63.4139 16.2337 63.694 15.8939 64.0785 15.642C64.4663 15.3902 64.9452 15.2642 65.5153 15.2642C66.0423 15.2642 66.5013 15.3769 66.8924 15.6023C67.2868 15.8277 67.5917 16.1541 67.8072 16.5817C68.0259 17.0092 68.1336 17.5279 68.1303 18.1378V23H66.3306V18.4162C66.3306 17.9058 66.198 17.5064 65.9329 17.218C65.671 16.9297 65.3081 16.7855 64.8441 16.7855C64.5292 16.7855 64.2492 16.8551 64.0039 16.9943C63.762 17.1302 63.5714 17.3274 63.4322 17.5859C63.2963 17.8445 63.2283 18.1577 63.2283 18.5256ZM72.1573 23.1541C71.6734 23.1541 71.2376 23.0679 70.8498 22.8956C70.4653 22.7199 70.1604 22.4614 69.935 22.12C69.7129 21.7786 69.6019 21.3577 69.6019 20.8572C69.6019 20.4264 69.6815 20.0701 69.8406 19.7884C69.9996 19.5066 70.2167 19.2812 70.4918 19.1122C70.7669 18.9432 71.0768 18.8156 71.4215 18.7294C71.7695 18.6399 72.1291 18.5753 72.5004 18.5355C72.9478 18.4891 73.3107 18.4477 73.5891 18.4112C73.8675 18.3714 74.0697 18.3118 74.1957 18.2322C74.3249 18.1494 74.3896 18.0218 74.3896 17.8494V17.8196C74.3896 17.4451 74.2785 17.1551 74.0565 16.9496C73.8344 16.7441 73.5146 16.6413 73.0969 16.6413C72.6561 16.6413 72.3065 16.7375 72.0479 16.9297C71.7927 17.1219 71.6204 17.349 71.5309 17.6108L69.8505 17.3722C69.9831 16.9081 70.2018 16.5204 70.5067 16.2088C70.8117 15.8939 71.1845 15.6586 71.6254 15.5028C72.0662 15.3438 72.5534 15.2642 73.087 15.2642C73.4549 15.2642 73.8211 15.3073 74.1857 15.3935C74.5503 15.4796 74.8834 15.6222 75.185 15.821C75.4866 16.0166 75.7286 16.2834 75.9109 16.6214C76.0965 16.9595 76.1893 17.3821 76.1893 17.8892V23H74.4592V21.951H74.3995C74.2901 22.1631 74.136 22.362 73.9371 22.5476C73.7416 22.7299 73.4947 22.8774 73.1964 22.9901C72.9014 23.0994 72.555 23.1541 72.1573 23.1541ZM72.6246 21.8317C72.9859 21.8317 73.2991 21.7604 73.5643 21.6179C73.8294 21.4721 74.0333 21.2798 74.1758 21.0412C74.3216 20.8026 74.3945 20.5424 74.3945 20.2607V19.3608C74.3382 19.4072 74.2421 19.4503 74.1062 19.4901C73.9736 19.5298 73.8245 19.5646 73.6587 19.5945C73.493 19.6243 73.329 19.6508 73.1665 19.674C73.0041 19.6972 72.8633 19.7171 72.744 19.7337C72.4755 19.7701 72.2352 19.8298 72.0231 19.9126C71.811 19.9955 71.6436 20.1115 71.521 20.2607C71.3983 20.4065 71.337 20.5954 71.337 20.8274C71.337 21.1589 71.458 21.4091 71.6999 21.5781C71.9419 21.7472 72.2501 21.8317 72.6246 21.8317ZM78.0785 23V12.8182H79.8782V16.6264H79.9528C80.0456 16.4408 80.1765 16.2436 80.3455 16.0348C80.5146 15.8227 80.7433 15.642 81.0316 15.4929C81.32 15.3404 81.6879 15.2642 82.1353 15.2642C82.7253 15.2642 83.2572 15.415 83.7312 15.7166C84.2085 16.0149 84.5863 16.4574 84.8647 17.044C85.1464 17.6274 85.2873 18.3433 85.2873 19.1918C85.2873 20.0303 85.1497 20.7429 84.8746 21.3295C84.5996 21.9162 84.225 22.3636 83.7511 22.6719C83.2771 22.9801 82.7402 23.1342 82.1403 23.1342C81.7028 23.1342 81.3398 23.0613 81.0515 22.9155C80.7631 22.7696 80.5311 22.594 80.3555 22.3885C80.1831 22.1797 80.0489 21.9825 79.9528 21.7969H79.8484V23H78.0785ZM79.8434 19.1818C79.8434 19.6757 79.913 20.1082 80.0522 20.4794C80.1947 20.8506 80.3986 21.1406 80.6637 21.3494C80.9322 21.5549 81.257 21.6577 81.6381 21.6577C82.0359 21.6577 82.369 21.5516 82.6374 21.3395C82.9059 21.1241 83.1081 20.8307 83.244 20.4595C83.3832 20.085 83.4528 19.6591 83.4528 19.1818C83.4528 18.7079 83.3848 18.2869 83.2489 17.919C83.113 17.5511 82.9109 17.2628 82.6424 17.054C82.3739 16.8452 82.0392 16.7408 81.6381 16.7408C81.2537 16.7408 80.9272 16.8419 80.6587 17.044C80.3903 17.2462 80.1864 17.5296 80.0472 17.8942C79.9113 18.2588 79.8434 18.688 79.8434 19.1818ZM88.617 12.8182V23H86.8173V12.8182H88.617ZM93.8484 23.1491C93.0827 23.1491 92.4215 22.9901 91.8647 22.6719C91.3112 22.3504 90.8853 21.8963 90.587 21.3097C90.2887 20.7197 90.1396 20.0253 90.1396 19.2266C90.1396 18.4411 90.2887 17.7517 90.587 17.1584C90.8886 16.5618 91.3095 16.0978 91.8498 15.7663C92.39 15.4316 93.0247 15.2642 93.7539 15.2642C94.2246 15.2642 94.6687 15.3404 95.0863 15.4929C95.5072 15.642 95.8784 15.8741 96.1999 16.1889C96.5247 16.5038 96.7799 16.9048 96.9656 17.392C97.1512 17.8759 97.244 18.4527 97.244 19.1222V19.674H90.9847V18.4609H95.5188C95.5155 18.1162 95.4409 17.8097 95.2951 17.5412C95.1493 17.2694 94.9454 17.0556 94.6836 16.8999C94.4251 16.7441 94.1235 16.6662 93.7788 16.6662C93.4109 16.6662 93.0877 16.7557 92.8093 16.9347C92.5309 17.1103 92.3138 17.3423 92.158 17.6307C92.0056 17.9157 91.9277 18.2289 91.9244 18.5703V19.6293C91.9244 20.0734 92.0056 20.4545 92.168 20.7727C92.3304 21.0876 92.5574 21.3295 92.8491 21.4986C93.1407 21.6643 93.4821 21.7472 93.8732 21.7472C94.1351 21.7472 94.372 21.7107 94.5842 21.6378C94.7963 21.5616 94.9802 21.4505 95.136 21.3047C95.2918 21.1589 95.4094 20.9782 95.489 20.7628L97.1694 20.9517C97.0633 21.3958 96.8612 21.7836 96.5629 22.1151C96.2679 22.4432 95.89 22.6984 95.4293 22.8807C94.9686 23.0597 94.4416 23.1491 93.8484 23.1491ZM101.6 23.1342C101 23.1342 100.463 22.9801 99.9895 22.6719C99.5156 22.3636 99.141 21.9162 98.8659 21.3295C98.5908 20.7429 98.4533 20.0303 98.4533 19.1918C98.4533 18.3433 98.5925 17.6274 98.8709 17.044C99.1526 16.4574 99.5321 16.0149 100.009 15.7166C100.487 15.415 101.019 15.2642 101.605 15.2642C102.053 15.2642 102.421 15.3404 102.709 15.4929C102.997 15.642 103.226 15.8227 103.395 16.0348C103.564 16.2436 103.695 16.4408 103.788 16.6264H103.862V12.8182H105.667V23H103.897V21.7969H103.788C103.695 21.9825 103.561 22.1797 103.385 22.3885C103.209 22.594 102.977 22.7696 102.689 22.9155C102.401 23.0613 102.038 23.1342 101.6 23.1342ZM102.102 21.6577C102.484 21.6577 102.808 21.5549 103.077 21.3494C103.345 21.1406 103.549 20.8506 103.688 20.4794C103.828 20.1082 103.897 19.6757 103.897 19.1818C103.897 18.688 103.828 18.2588 103.688 17.8942C103.552 17.5296 103.35 17.2462 103.082 17.044C102.817 16.8419 102.49 16.7408 102.102 16.7408C101.701 16.7408 101.367 16.8452 101.098 17.054C100.83 17.2628 100.628 17.5511 100.492 17.919C100.356 18.2869 100.288 18.7079 100.288 19.1818C100.288 19.6591 100.356 20.085 100.492 20.4595C100.631 20.8307 100.835 21.1241 101.103 21.3395C101.375 21.5516 101.708 21.6577 102.102 21.6577Z"
                                      fill="white"
                                    />
                                    <defs>
                                      <radialGradient
                                        id="paint0_radial_5892_1829"
                                        cx="0"
                                        cy="0"
                                        r="1"
                                        gradientUnits="userSpaceOnUse"
                                        gradientTransform="translate(64.5446) rotate(90) scale(36 115.83)"
                                      >
                                        <stop stop-color="#2E2E2E" />
                                        <stop offset="1" stop-color="#474747" />
                                      </radialGradient>
                                      <linearGradient
                                        id="paint1_linear_5892_1829"
                                        x1="61.5"
                                        y1="0"
                                        x2="61.5"
                                        y2="36"
                                        gradientUnits="userSpaceOnUse"
                                      >
                                        <stop stop-color="#9B9DB6" />
                                        <stop
                                          offset="0.216346"
                                          stop-color="#D6D7E1"
                                        />
                                        <stop
                                          offset="0.528846"
                                          stop-color="#F3F3F6"
                                        />
                                        <stop
                                          offset="0.831731"
                                          stop-color="#D6D7E1"
                                        />
                                        <stop offset="1" stop-color="#9B9DB6" />
                                      </linearGradient>
                                    </defs>
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  className=""
                                  onClick={() => toggleThemeStatus(t._id)}
                                >
                                  <svg
                                    width="148"
                                    height="36"
                                    viewBox="0 0 148 36"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      width="148"
                                      height="36"
                                      rx="10"
                                      fill="url(#paint0_radial_5892_1798)"
                                      fill-opacity="0.2"
                                    />
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="147"
                                      height="35"
                                      rx="9.5"
                                      stroke="url(#paint1_linear_5892_1798)"
                                      stroke-opacity="0.88"
                                    />
                                    <path
                                      d="M27 18C27 19.6569 25.6568 21 24 21C22.3432 21 21 19.6569 21 18C21 16.3431 22.3432 15 24 15C25.6568 15 27 16.3431 27 18Z"
                                      stroke="#F3F3F6"
                                      stroke-width="1.5"
                                    />
                                    <path
                                      d="M32 12H24C20.6863 12 18 14.6863 18 18C18 21.3137 20.6863 24 24 24H32C35.3137 24 38 21.3137 38 18C38 14.6863 35.3137 12 32 12Z"
                                      stroke="#F3F3F6"
                                      stroke-width="1.5"
                                    />
                                    <path
                                      d="M53.0043 23V12.8182H59.6264V14.3643H54.8487V17.1286H59.2834V18.6747H54.8487V21.4538H59.6662V23H53.0043ZM63.2283 18.5256V23H61.4286V15.3636H63.1488V16.6612H63.2383C63.4139 16.2337 63.694 15.8939 64.0785 15.642C64.4663 15.3902 64.9452 15.2642 65.5153 15.2642C66.0423 15.2642 66.5013 15.3769 66.8924 15.6023C67.2868 15.8277 67.5917 16.1541 67.8072 16.5817C68.0259 17.0092 68.1336 17.5279 68.1303 18.1378V23H66.3306V18.4162C66.3306 17.9058 66.198 17.5064 65.9329 17.218C65.671 16.9297 65.3081 16.7855 64.8441 16.7855C64.5292 16.7855 64.2492 16.8551 64.0039 16.9943C63.762 17.1302 63.5714 17.3274 63.4322 17.5859C63.2963 17.8445 63.2283 18.1577 63.2283 18.5256ZM72.1573 23.1541C71.6734 23.1541 71.2376 23.0679 70.8498 22.8956C70.4653 22.7199 70.1604 22.4614 69.935 22.12C69.7129 21.7786 69.6019 21.3577 69.6019 20.8572C69.6019 20.4264 69.6815 20.0701 69.8406 19.7884C69.9996 19.5066 70.2167 19.2812 70.4918 19.1122C70.7669 18.9432 71.0768 18.8156 71.4215 18.7294C71.7695 18.6399 72.1291 18.5753 72.5004 18.5355C72.9478 18.4891 73.3107 18.4477 73.5891 18.4112C73.8675 18.3714 74.0697 18.3118 74.1957 18.2322C74.3249 18.1494 74.3896 18.0218 74.3896 17.8494V17.8196C74.3896 17.4451 74.2785 17.1551 74.0565 16.9496C73.8344 16.7441 73.5146 16.6413 73.0969 16.6413C72.6561 16.6413 72.3065 16.7375 72.0479 16.9297C71.7927 17.1219 71.6204 17.349 71.5309 17.6108L69.8505 17.3722C69.9831 16.9081 70.2018 16.5204 70.5067 16.2088C70.8117 15.8939 71.1845 15.6586 71.6254 15.5028C72.0662 15.3438 72.5534 15.2642 73.087 15.2642C73.4549 15.2642 73.8211 15.3073 74.1857 15.3935C74.5503 15.4796 74.8834 15.6222 75.185 15.821C75.4866 16.0166 75.7286 16.2834 75.9109 16.6214C76.0965 16.9595 76.1893 17.3821 76.1893 17.8892V23H74.4592V21.951H74.3995C74.2901 22.1631 74.136 22.362 73.9371 22.5476C73.7416 22.7299 73.4947 22.8774 73.1964 22.9901C72.9014 23.0994 72.555 23.1541 72.1573 23.1541ZM72.6246 21.8317C72.9859 21.8317 73.2991 21.7604 73.5643 21.6179C73.8294 21.4721 74.0333 21.2798 74.1758 21.0412C74.3216 20.8026 74.3945 20.5424 74.3945 20.2607V19.3608C74.3382 19.4072 74.2421 19.4503 74.1062 19.4901C73.9736 19.5298 73.8245 19.5646 73.6587 19.5945C73.493 19.6243 73.329 19.6508 73.1665 19.674C73.0041 19.6972 72.8633 19.7171 72.744 19.7337C72.4755 19.7701 72.2352 19.8298 72.0231 19.9126C71.811 19.9955 71.6436 20.1115 71.521 20.2607C71.3983 20.4065 71.337 20.5954 71.337 20.8274C71.337 21.1589 71.458 21.4091 71.6999 21.5781C71.9419 21.7472 72.2501 21.8317 72.6246 21.8317ZM78.0785 23V12.8182H79.8782V16.6264H79.9528C80.0456 16.4408 80.1765 16.2436 80.3455 16.0348C80.5146 15.8227 80.7433 15.642 81.0316 15.4929C81.32 15.3404 81.6879 15.2642 82.1353 15.2642C82.7253 15.2642 83.2572 15.415 83.7312 15.7166C84.2085 16.0149 84.5863 16.4574 84.8647 17.044C85.1464 17.6274 85.2873 18.3433 85.2873 19.1918C85.2873 20.0303 85.1497 20.7429 84.8746 21.3295C84.5996 21.9162 84.225 22.3636 83.7511 22.6719C83.2771 22.9801 82.7402 23.1342 82.1403 23.1342C81.7028 23.1342 81.3398 23.0613 81.0515 22.9155C80.7631 22.7696 80.5311 22.594 80.3555 22.3885C80.1831 22.1797 80.0489 21.9825 79.9528 21.7969H79.8484V23H78.0785ZM79.8434 19.1818C79.8434 19.6757 79.913 20.1082 80.0522 20.4794C80.1947 20.8506 80.3986 21.1406 80.6637 21.3494C80.9322 21.5549 81.257 21.6577 81.6381 21.6577C82.0359 21.6577 82.369 21.5516 82.6374 21.3395C82.9059 21.1241 83.1081 20.8307 83.244 20.4595C83.3832 20.085 83.4528 19.6591 83.4528 19.1818C83.4528 18.7079 83.3848 18.2869 83.2489 17.919C83.113 17.5511 82.9109 17.2628 82.6424 17.054C82.3739 16.8452 82.0392 16.7408 81.6381 16.7408C81.2537 16.7408 80.9272 16.8419 80.6587 17.044C80.3903 17.2462 80.1864 17.5296 80.0472 17.8942C79.9113 18.2588 79.8434 18.688 79.8434 19.1818ZM88.617 12.8182V23H86.8173V12.8182H88.617ZM93.8484 23.1491C93.0827 23.1491 92.4215 22.9901 91.8647 22.6719C91.3112 22.3504 90.8853 21.8963 90.587 21.3097C90.2887 20.7197 90.1396 20.0253 90.1396 19.2266C90.1396 18.4411 90.2887 17.7517 90.587 17.1584C90.8886 16.5618 91.3095 16.0978 91.8498 15.7663C92.39 15.4316 93.0247 15.2642 93.7539 15.2642C94.2246 15.2642 94.6687 15.3404 95.0863 15.4929C95.5072 15.642 95.8784 15.8741 96.1999 16.1889C96.5247 16.5038 96.7799 16.9048 96.9656 17.392C97.1512 17.8759 97.244 18.4527 97.244 19.1222V19.674H90.9847V18.4609H95.5188C95.5155 18.1162 95.4409 17.8097 95.2951 17.5412C95.1493 17.2694 94.9454 17.0556 94.6836 16.8999C94.4251 16.7441 94.1235 16.6662 93.7788 16.6662C93.4109 16.6662 93.0877 16.7557 92.8093 16.9347C92.5309 17.1103 92.3138 17.3423 92.158 17.6307C92.0056 17.9157 91.9277 18.2289 91.9244 18.5703V19.6293C91.9244 20.0734 92.0056 20.4545 92.168 20.7727C92.3304 21.0876 92.5574 21.3295 92.8491 21.4986C93.1407 21.6643 93.4821 21.7472 93.8732 21.7472C94.1351 21.7472 94.372 21.7107 94.5842 21.6378C94.7963 21.5616 94.9802 21.4505 95.136 21.3047C95.2918 21.1589 95.4094 20.9782 95.489 20.7628L97.1694 20.9517C97.0633 21.3958 96.8612 21.7836 96.5629 22.1151C96.2679 22.4432 95.89 22.6984 95.4293 22.8807C94.9686 23.0597 94.4416 23.1491 93.8484 23.1491ZM110.686 12.8182V23H109.045L104.248 16.0646H104.163V23H102.319V12.8182H103.969L108.762 19.7585H108.851V12.8182H110.686ZM115.947 23.1491C115.201 23.1491 114.555 22.9851 114.008 22.657C113.461 22.3288 113.037 21.8698 112.735 21.2798C112.437 20.6899 112.288 20.0005 112.288 19.2116C112.288 18.4228 112.437 17.7318 112.735 17.1385C113.037 16.5452 113.461 16.0845 114.008 15.7564C114.555 15.4283 115.201 15.2642 115.947 15.2642C116.693 15.2642 117.339 15.4283 117.886 15.7564C118.433 16.0845 118.855 16.5452 119.154 17.1385C119.455 17.7318 119.606 18.4228 119.606 19.2116C119.606 20.0005 119.455 20.6899 119.154 21.2798C118.855 21.8698 118.433 22.3288 117.886 22.657C117.339 22.9851 116.693 23.1491 115.947 23.1491ZM115.957 21.7074C116.361 21.7074 116.699 21.5964 116.971 21.3743C117.243 21.1489 117.445 20.8473 117.578 20.4695C117.714 20.0916 117.782 19.6707 117.782 19.2067C117.782 18.7393 117.714 18.3168 117.578 17.9389C117.445 17.5578 117.243 17.2545 116.971 17.0291C116.699 16.8037 116.361 16.6911 115.957 16.6911C115.543 16.6911 115.198 16.8037 114.923 17.0291C114.651 17.2545 114.447 17.5578 114.311 17.9389C114.179 18.3168 114.113 18.7393 114.113 19.2067C114.113 19.6707 114.179 20.0916 114.311 20.4695C114.447 20.8473 114.651 21.1489 114.923 21.3743C115.198 21.5964 115.543 21.7074 115.957 21.7074ZM122.491 23L120.333 15.3636H122.168L123.51 20.733H123.58L124.952 15.3636H126.767L128.139 20.7031H128.213L129.536 15.3636H131.375L129.213 23H127.338L125.906 17.8395H125.802L124.37 23H122.491Z"
                                      fill="white"
                                    />
                                    <defs>
                                      <radialGradient
                                        id="paint0_radial_5892_1798"
                                        cx="0"
                                        cy="0"
                                        r="1"
                                        gradientUnits="userSpaceOnUse"
                                        gradientTransform="translate(77.6634) rotate(90) scale(36 139.373)"
                                      >
                                        <stop stop-color="#C6D92C" />
                                        <stop offset="1" stop-color="#535B10" />
                                      </radialGradient>
                                      <linearGradient
                                        id="paint1_linear_5892_1798"
                                        x1="0"
                                        y1="18"
                                        x2="148"
                                        y2="18"
                                        gradientUnits="userSpaceOnUse"
                                      >
                                        <stop stop-color="#C6D92C" />
                                        <stop
                                          offset="0.533654"
                                          stop-color="#EDF5C0"
                                        />
                                        <stop offset="1" stop-color="#C6D92C" />
                                      </linearGradient>
                                    </defs>
                                  </svg>
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        {/* Preview Placeholder SVG */}
                        <button
                          className=""
                          onClick={() => {
                            handlePreviewThemeClick(t);
                          }}
                        >
                          <svg
                            width="25"
                            height="21"
                            viewBox="0 0 25 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M22.7629 0H1.45295C0.653828 0 0 0.653828 0 1.45295V17.4354C0 18.2345 0.653828 18.8884 1.45295 18.8884H7.04682V20.0314H5.62292C5.35655 20.0314 5.13861 20.2493 5.13861 20.5157C5.13861 20.7821 5.35655 21 5.62292 21H7.53113H16.6847H18.5929C18.8593 21 19.0773 20.7821 19.0773 20.5157C19.0773 20.2493 18.8593 20.0314 18.5929 20.0314H17.169V18.8884H22.7629C23.562 18.8884 24.2159 18.2345 24.2159 17.4354V1.45295C24.2159 0.653828 23.562 0 22.7629 0ZM16.2004 20.0314H8.01545V18.8884H16.2004V20.0314ZM23.2472 17.4354C23.2472 17.7018 23.0293 17.9197 22.7629 17.9197H16.6847H7.53113H1.45295C1.18658 17.9197 0.968634 17.7018 0.968634 17.4354V1.45295C0.968634 1.18658 1.18658 0.968635 1.45295 0.968635H22.7629C23.0293 0.968635 23.2472 1.18658 23.2472 1.45295V17.4354Z"
                              fill="white"
                            />
                            <path
                              d="M12.1077 4.77551C8.96447 4.77551 6.02951 6.47062 4.45064 9.20217C4.36346 9.35231 4.36346 9.53635 4.45064 9.68649C6.03435 12.418 8.96932 14.1131 12.1077 14.1131C15.2461 14.1131 18.1859 12.418 19.7647 9.68649C19.8519 9.53635 19.8519 9.35231 19.7647 9.20217C18.1859 6.47062 15.2509 4.77551 12.1077 4.77551ZM12.1077 13.1445C9.4052 13.1445 6.87222 11.7352 5.4338 9.44433C6.87222 7.15351 9.4052 5.74415 12.1077 5.74415C14.8102 5.74415 17.3432 7.15351 18.7816 9.44433C17.3432 11.7352 14.8102 13.1445 12.1077 13.1445Z"
                              fill="#C6D92C"
                            />
                            <path
                              d="M12.1079 6.82349C10.6647 6.82349 9.48779 8.00038 9.48779 9.44364C9.48779 10.8869 10.6647 12.0638 12.1079 12.0638C13.5512 12.0638 14.7281 10.8869 14.7281 9.44364C14.7281 8.00038 13.5512 6.82349 12.1079 6.82349ZM12.1079 11.0952C11.1974 11.0952 10.4564 10.3542 10.4564 9.44364C10.4564 8.53313 11.1974 7.79212 12.1079 7.79212C13.0185 7.79212 13.7595 8.53313 13.7595 9.44364C13.7595 10.3542 13.0185 11.0952 12.1079 11.0952Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                        {!t.isDefault && (
                          // {/* Edit Placeholder SVG */}
                          <>
                            <button
                              className=""
                              onClick={() => {
                                handleEditThemeClick(t._id);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                                  stroke="#C6D92C"
                                  strokeWidth="1.5"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                                  stroke="#F3F3F6"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>

                            {/* Delete Placeholder SVG */}
                            <button
                              className=""
                              onClick={() => {
                                setShowDeleteModal(true);
                                setSelectedTheme(t);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                                  fill="url(#paint0_linear_315_5333)"
                                />
                                <path
                                  d="M9.5 16.5V10.5"
                                  stroke="url(#paint1_linear_315_5333)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M14.5 16.5V10.5"
                                  stroke="url(#paint2_linear_315_5333)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <defs>
                                  <linearGradient
                                    id="paint0_linear_315_5333"
                                    x1="12"
                                    y1="1.25"
                                    x2="12"
                                    y2="22.75"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#D92626" />
                                    <stop offset="1" stop-color="#AD1F1F" />
                                  </linearGradient>
                                  <linearGradient
                                    id="paint1_linear_315_5333"
                                    x1="10"
                                    y1="10.5"
                                    x2="10"
                                    y2="16.5"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#D92626" />
                                    <stop offset="1" stop-color="#AD1F1F" />
                                  </linearGradient>
                                  <linearGradient
                                    id="paint2_linear_315_5333"
                                    x1="15"
                                    y1="10.5"
                                    x2="15"
                                    y2="16.5"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#D92626" />
                                    <stop offset="1" stop-color="#AD1F1F" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <DeleteThemeModal
        isOpen={showDeleteModal}
        themeMode="dark"
        themeName={selectedTheme?.name}
        isDeleting={deleteLoading}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteTheme}
      />

      {/* Live Preview Modal Overlay */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          {/* Modal Container */}
          <div className="bg-[#1A1A1A] rounded-xl shadow-2xl relative w-[70vw] h-[80vh] flex flex-col border border-gray-700">
            {/* Header / Close Button */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                <span>Theme Preview</span>
                {previewThemeData?.name && (
                  <span className="text-sm font-normal text-gray-400">
                    ({previewThemeData.name})
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-4 mr-2">
                {/* Device Selector */}
                <div className="relative">
                  <select
                    value={previewDevice}
                    onChange={(e) => setPreviewDevice(e.target.value as "tv" | "staff")}
                    className="bg-[#111] border border-[#333] text-white text-xs font-semibold rounded-lg pl-3 pr-8 py-1.5 outline-none cursor-pointer appearance-none focus:border-[#C6D92C] transition-colors"
                  >
                    <option value="tv" className="bg-[#111]">TV </option>
                    <option value="staff" className="bg-[#111]">USER</option>
                  </select>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </div>

                {/* Mode Selector */}
                {previewDevice === "staff" && (
                  <div className="relative">
                    <select
                      value={previewMode}
                      onChange={(e) => setPreviewMode(e.target.value as "dark" | "light")}
                      className="bg-[#111] border border-[#333] text-white text-xs font-semibold rounded-lg pl-3 pr-8 py-1.5 outline-none cursor-pointer appearance-none focus:border-[#C6D92C] transition-colors"
                    >
                      <option value="dark" className="bg-[#111]">Dark Mode</option>
                      <option value="light" className="bg-[#111]">Light Mode</option>
                    </select>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                    >
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  </div>
                )}
              </div>
              <button
                className="text-gray-400 hover:text-white transition-colors"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewThemeData(null);
                }}
              >
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Iframe Body */}
            <div className="flex-1 w-full h-full overflow-hidden rounded-b-xl bg-black">
              {previewUrl && (
                <ScaledIframe
                  src={previewUrl}
                  ref={iframeRef}
                  device={previewDevice}
                />
              )}
            </div>

            <div className="px-3 py-2 flex items-center justify-center">
              <button
                className="flex items-center gap-2 px-6 py-2.5 border border-[#333] bg-[#222] hover:bg-[#2A2A2A] rounded-lg text-sm font-medium transition-colors text-white shadow-sm"
                onClick={() => iframeRef.current?.enterFullscreen()}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Preview Full Screen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeSettings;
